import { 
  collection, 
  getDocs, 
  query, 
  where, 
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { DEFAULT_ONGOING_PROJECT } from '../constants/ongoingProjectConstants';
import { sanitizeOngoingProject } from '../utils/ongoingProjectValidation';

const ONGOING_PROJECTS_COLLECTION = 'ongoingProjects';

export const ongoingProjectService = {
  
  // Get ongoing projects with optional filters
  async getOngoingProjects(financialYear = null, department = null) {
    try {
      let projectsQuery = collection(db, ONGOING_PROJECTS_COLLECTION);
      
      const constraints = [];
      
      if (financialYear) {
        constraints.push(where('financialYear', '==', financialYear));
      }
      
      if (department) {
        constraints.push(where('department', '==', department));
      }
      
      // Sort by department first, then by creation date
      constraints.push(orderBy('department', 'asc'));
      constraints.push(orderBy('createdAt', 'desc'));
      
      if (constraints.length > 0) {
        projectsQuery = query(projectsQuery, ...constraints);
      }
      
      const querySnapshot = await getDocs(projectsQuery);
      const projects = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        projects.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
          createdBy: data.createdBy || null,
          updatedBy: data.updatedBy || null
        });
      });
      
      return { success: true, data: projects };
    } catch (error) {
      console.error('Error fetching ongoing projects:', error);
      throw new Error('Failed to load ongoing projects: ' + error.message);
    }
  },

  // Add new ongoing project
  async addOngoingProject(projectData, userId) {
    try {
      const sanitizedData = sanitizeOngoingProject(projectData);
      
      const dataToSave = {
        ...DEFAULT_ONGOING_PROJECT,
        ...sanitizedData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId
      };
      
      const docRef = await addDoc(collection(db, ONGOING_PROJECTS_COLLECTION), dataToSave);
      
      console.log('Ongoing project added with ID:', docRef.id);
      return { success: true, data: { id: docRef.id, ...dataToSave } };
    } catch (error) {
      console.error('Error adding ongoing project:', error);
      throw new Error('Failed to add ongoing project: ' + error.message);
    }
  },

  // Update existing ongoing project
  async updateOngoingProject(projectId, projectData, userId) {
    try {
      const sanitizedData = sanitizeOngoingProject(projectData);
      
      const updateData = {
        ...sanitizedData,
        updatedAt: serverTimestamp(),
        updatedBy: userId
      };
      
      const projectRef = doc(db, ONGOING_PROJECTS_COLLECTION, projectId);
      await updateDoc(projectRef, updateData);
      
      console.log('Ongoing project updated:', projectId);
      return { success: true, data: updateData };
    } catch (error) {
      console.error('Error updating ongoing project:', error);
      throw new Error('Failed to update ongoing project: ' + error.message);
    }
  },

  // Delete ongoing project
  async deleteOngoingProject(projectId) {
    try {
      const projectRef = doc(db, ONGOING_PROJECTS_COLLECTION, projectId);
      await deleteDoc(projectRef);
      
      console.log('Ongoing project deleted:', projectId);
      return { success: true };
    } catch (error) {
      console.error('Error deleting ongoing project:', error);
      throw new Error('Failed to delete ongoing project: ' + error.message);
    }
  },

  // Get ongoing projects for specific department (department user)
  async getOngoingProjectsByDepartment(department, financialYear = null) {
    try {
      return await this.getOngoingProjects(financialYear, department);
    } catch (error) {
      console.error('Error fetching department ongoing projects:', error);
      throw new Error('Failed to load department ongoing projects: ' + error.message);
    }
  },

  // Get all ongoing projects (admin view)
  async getAllOngoingProjects(financialYear = null) {
    try {
      return await this.getOngoingProjects(financialYear, null);
    } catch (error) {
      console.error('Error fetching all ongoing projects:', error);
      throw new Error('Failed to load all ongoing projects: ' + error.message);
    }
  },

  // Calculate department totals
  calculateDepartmentTotals(projects) {
    const departmentTotals = {};
    
    projects.forEach(project => {
      if (!project.department) return;
      
      if (!departmentTotals[project.department]) {
        departmentTotals[project.department] = {
          department: project.department,
          totalEstimatedCost: 0,
          totalExpenditure: 0,
          balanceRemaining: 0,
          projectCount: 0
        };
      }
      
      const dept = departmentTotals[project.department];
      dept.totalEstimatedCost += parseFloat(project.totalEstimatedCost) || 0;
      dept.totalExpenditure += parseFloat(project.totalExpenditure) || 0;
      dept.balanceRemaining += parseFloat(project.balanceRemaining) || 0;
      dept.projectCount += 1;
    });
    
    return Object.values(departmentTotals);
  }
};

export default ongoingProjectService;